export type ThemeMode = "system" | "light" | "dark";
export type UserRole = "user" | "admin";
export type ConnectionStatus = "online" | "sleep" | "offline";
export type CommandType =
  | "UP"
  | "DOWN"
  | "POWER_OFF"
  | "LIGHT_OFF"
  | "LIGHT_ON"
  | "LIGHT_AUTO"
  | "BUZZER_OFF"
  | "BUZZER_ON"
  | "BUZZER_AUTO"
  | "STATUS_REFRESH"
  | "GPS_LOCATION";

export interface BuoyState {
  online: boolean;
  connectionStatus: ConnectionStatus;
  lastSeenAt: string | null;
  latitude: string | null;
  longitude: string | null;
  accuracyM: string | null;
  satellitesVisible: number | null;
  currentLocationKnown: boolean;
  lastGpsAttemptAt: string | null;
  lastGpsFixAt: string | null;
  batteryPct: number | null;
  batteryVoltageMv: number | null;
  batteryRawSoc: number | null;
  batteryAdcVoltageMv: number | null;
  endSwitchClosed: boolean | null;
  motorTemperatureCentiC: number | null;
  buoyTemperatureCentiC: number | null;
  signalDbm: number | null;
  updatedAt: string;
}

export function connectionStatusOf(
  state: Pick<BuoyState, "online" | "connectionStatus"> | null | undefined,
): ConnectionStatus {
  return state?.online ? "online" : (state?.connectionStatus ?? "offline");
}

export interface Buoy {
  id: string;
  serial: string;
  deviceId: string;
  model: string;
  name: string;
  lifecycle: "provisioned" | "active" | "suspended" | "retired";
  firmwareVersion: string | null;
  ownerUserId: string | null;
  state: BuoyState | null;
  factory?: {
    chipMac: string | null;
    modemImei: string | null;
  };
  sim?: {
    iccid: string;
    imsi: string | null;
    status: string | null;
    provider: string | null;
    msisdn: string | null;
    operator: string | null;
    operatorAccessTechnology: number | null;
  } | null;
}

export interface FirmwareStatus {
  product: string;
  currentVersion: string | null;
  available: boolean;
  supportsRemoteUpdate: boolean;
  canInstall: boolean;
  availableVersion: string | null;
  releaseId: string | null;
  update: {
    commandId: string;
    targetVersion: string;
    phase: "sending" | "installing" | "succeeded" | "failed";
    startedAt: string;
    errorCode: string | null;
  } | null;
}

export interface TrackPoint {
  recordedAt: string;
  latitude: number;
  longitude: number;
  accuracyM?: number;
  satellitesVisible?: number | null;
}

export interface DeviceCommand {
  id: string;
  type: string;
  status:
    "queued" | "published" | "acknowledged" | "rejected" | "failed" | "expired";
  errorCode: string | null;
  createdAt: string;
}

export interface ApiPage<T> {
  items: T[];
  nextCursor: string | null;
}

export interface FirmwareRelease {
  schemaVersion: 1;
  id: string;
  product: string;
  version: string;
  profile: "esp32-a7670";
  chip: "ESP32";
  flashBytes: number;
  partition: { offset: number; size: number; sha256: string };
  filesystem: {
    offset: number;
    size: number;
    blockSize: number;
    pageSize: number;
  };
  images: {
    name: string;
    offset: number;
    size: number;
    sha256: string;
    update: boolean;
  }[];
}
export type FactoryMode = "provision" | "update" | "replace-credentials";
export interface FactorySimIdentity {
  status: string;
  iccid: string;
  imsi: string | null;
  modemImei: string;
  provider: string | null;
  msisdn: string | null;
  operator: string | null;
  operatorAccessTechnology: number | null;
}
export interface FactoryJob {
  id: string;
  buoyId: string;
  serial: string;
  chipMac: string;
  mode: FactoryMode;
  status: "prepared" | "flashed" | "ready" | "completed" | "expired";
  release: FirmwareRelease;
  expiresAt: string;
  hasLabel: boolean;
  claimCode: string | null;
  flashStartedAt: string | null;
  mqttConnected: boolean;
  sim: FactorySimIdentity | null;
  initialize?: boolean;
}
